import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Badge,
  Button,
  Spinner,
  Divider,
  Banner,
  IndexTable,
  LegacyCard,
  InlineStack,
  Modal,
  TextField,
  Select,
} from "@shopify/polaris";
import { useParams, useNavigate } from "react-router-dom";
import { formatPrice, formatDate, formatPlate } from "../../utility/funcs";
import { generateGroupInvoice, getNextTriggerDate, isTriggerDate } from "../../utility/InvoicingEngine";
import { addDaysISO } from "../../utility/invoicing";

function InvoicingGroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [cars, setCars] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [form, setForm] = useState({});
  const [company, setCompany] = useState({ id: null, name: "" });
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    const { data: g } = await supabase
      .from("invoicing_groups")
      .select("*, company:company_id(name, rfc)")
      .eq("id", groupId)
      .single();
    setGroup(g);

    // Active reservations (cars) in this group
    const { data: reservations } = await supabase
      .from("reservations")
      .select("id, status, date_range, totals, car(plate, model(name, brand(name))), customer(first_name, last_name)")
      .eq("invoicing_group_id", groupId)
      .order("created_at", { ascending: false });
    setCars(reservations || []);

    // Invoices linked to reservations in this group
    const resIds = (reservations || []).map(r => r.id);
    if (resIds.length > 0) {
      const { data: invs } = await supabase
        .from("invoices_issued")
        .select("*")
        .in("reservation_id", resIds)
        .order("date", { ascending: false });
      setInvoices(invs || []);
    }

    // Auto invoice runs
    const { data: runsData } = await supabase
      .from("auto_invoice_runs")
      .select("*, invoice:invoice_id(invoice_number, total, status)")
      .eq("invoicing_group_id", groupId)
      .order("created_at", { ascending: false })
      .limit(20);
    setRuns(runsData || []);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleGenerate = async (stamp) => {
    setGenerating(true);
    setResult(null);
    try {
      const res = await generateGroupInvoice(group, new Date(), stamp);
      setResult({ success: true, ...res });
      fetchData();
    } catch (err) {
      setResult({ success: false, error: err.message });
    }
    setGenerating(false);
  };

  const openEdit = () => {
    setForm({
      name: group.name,
      periodicity: group.periodicity || "monthly_1st",
      weekly_day: group.weekly_day || 1,
      billing_period: group.billing_period || "transcurrido",
      credit_enabled: group.credit_enabled || false,
      credit_days: group.credit_days || 0,
      auto_mode: group.auto_mode || "manual",
    });
    setCompany({ id: group.company_id, name: group.company?.name || "" });
    setCompanySearch("");
    setCompanyResults([]);
    setEditModal(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    await supabase.from("invoicing_groups").update({
      name: form.name,
      company_id: company.id || null,
      periodicity: form.periodicity,
      weekly_day: form.periodicity === "weekly" ? form.weekly_day : null,
      billing_period: form.billing_period,
      credit_enabled: form.credit_enabled,
      credit_days: form.credit_enabled ? form.credit_days : 0,
      auto_mode: form.auto_mode,
    }).eq("id", group.id);
    setSavingEdit(false);
    setEditModal(false);
    fetchData();
  };

  if (loading) return <AppWrapper><Page title="Grupo"><Spinner /></Page></AppWrapper>;
  if (!group) return <AppWrapper><Page title="Grupo no encontrado" backAction={{ url: "/invoicing/auto" }} /></AppWrapper>;

  const nextTrigger = getNextTriggerDate(group.periodicity, group.weekly_day);
  const canInvoice = isTriggerDate(group.periodicity, group.weekly_day) || nextTrigger <= new Date();
  const activeCars = cars.filter(c => c.status === "active");
  const monthlyEstimate = activeCars.reduce((sum, r) => sum + (r.totals?.dailyPrice || 0) * 30, 0);

  const periodicityLabel = {
    monthly_1st: "1ero de cada mes",
    monthly_last: "Último de cada mes",
    weekly: "Semanal",
    biweekly: "Quincenal",
  }[group.periodicity] || group.periodicity;

  return (
    <AppWrapper>
      <Page
        title={group.name}
        subtitle={group.company?.name || ""}
        backAction={{ url: "/invoicing/auto", content: "Facturación" }}
        primaryAction={
          <Button onClick={openEdit}>
            Editar
          </Button>
        }
      >
        <BlockStack gap="400">
          {/* Group Info */}
          <Card>
            <InlineGrid columns={4} gap="200">
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Periodicidad</Text>
                <Text>{periodicityLabel}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Periodo</Text>
                <Text>{group.billing_period === "posterior" ? "Posterior" : "Transcurrido"}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Crédito</Text>
                <Text>{group.credit_enabled ? `${group.credit_days} días` : "Sin crédito"}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Modo</Text>
                <Badge size="small" tone={group.auto_mode === "auto_stamp" ? "success" : group.auto_mode === "auto_draft" ? "attention" : "default"}>
                  {{ manual: "Manual", auto_draft: "Auto Borrador", auto_stamp: "Auto Timbrar" }[group.auto_mode]}
                </Badge>
              </BlockStack>
            </InlineGrid>
            <br />
            <InlineGrid columns={3} gap="200">
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Autos Activos</Text>
                <Text variant="headingMd">{activeCars.length}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Estimado Mensual</Text>
                <Text variant="headingMd">{formatPrice(monthlyEstimate)}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Próximo Trigger</Text>
                <Text variant="headingMd">{formatDate(nextTrigger.toISOString().substring(0, 10))}</Text>
              </BlockStack>
            </InlineGrid>
          </Card>

          {/* Generate Invoice */}
          {result && (
            <Banner tone={result.success ? "success" : "critical"}>
              {result.success
                ? `Factura ${result.invoiceNumber} creada — ${formatPrice(result.total)} (${result.lineItems} conceptos)`
                : result.error}
            </Banner>
          )}

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd">Generar Factura</Text>
              {canInvoice ? (
                <InlineStack gap="200">
                  <Button onClick={() => handleGenerate(false)} loading={generating} disabled={generating || activeCars.length === 0}>
                    Generar Borrador
                  </Button>
                  <Button variant="primary" onClick={() => handleGenerate(true)} loading={generating} disabled={generating || activeCars.length === 0}>
                    Generar y Timbrar
                  </Button>
                </InlineStack>
              ) : (
                <Text tone="subdued">La siguiente fecha de facturación es {formatDate(nextTrigger.toISOString().substring(0, 10))}. No se puede facturar antes de esa fecha.</Text>
              )}
            </BlockStack>
          </Card>

          {/* Active Cars */}
          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd">Autos ({activeCars.length})</Text>
              {activeCars.map((r) => (
                <InlineGrid key={r.id} columns="auto 1fr auto auto" gap="200">
                  <Button variant="plain" onClick={() => navigate(`/reservation?id=${r.id}`)}>
                    #{r.id}
                  </Button>
                  <Text variant="bodySm">
                    {formatPlate(r.car?.plate)} — {r.car?.model?.brand?.name} {r.car?.model?.name}
                  </Text>
                  <Text variant="bodySm">{formatPrice(r.totals?.dailyPrice || 0)}/día</Text>
                  <Text variant="bodySm" tone="subdued">{formatPrice((r.totals?.dailyPrice || 0) * 30)}/mes</Text>
                </InlineGrid>
              ))}
              {cars.filter(c => c.status !== "active").length > 0 && (
                <>
                  <Divider />
                  <Text variant="bodySm" tone="subdued">Inactivos:</Text>
                  {cars.filter(c => c.status !== "active").map((r) => (
                    <InlineGrid key={r.id} columns="auto 1fr auto">
                      <Button variant="plain" onClick={() => navigate(`/reservation?id=${r.id}`)}>
                        #{r.id}
                      </Button>
                      <Text variant="bodySm" tone="subdued">
                        {formatPlate(r.car?.plate)} — {r.car?.model?.name}
                      </Text>
                      <Badge size="small">{r.status}</Badge>
                    </InlineGrid>
                  ))}
                </>
              )}
            </BlockStack>
          </Card>

          {/* Invoice History */}
          {runs.length > 0 && (
            <Card>
              <BlockStack gap="200">
                <Text variant="headingMd">Historial de Facturación</Text>
                <LegacyCard>
                  <IndexTable
                    resourceName={{ singular: "Factura", plural: "Facturas" }}
                    itemCount={runs.length}
                    selectable={false}
                    headings={[
                      { title: "Fecha" },
                      { title: "Periodo" },
                      { title: "Factura" },
                      { title: "Total" },
                      { title: "Estado" },
                    ]}
                  >
                    {runs.map((r, i) => (
                      <IndexTable.Row key={r.id} id={r.id} position={i}>
                        <IndexTable.Cell>
                          <Text variant="bodySm">{formatDate(r.trigger_date)}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {/* period_to is the last billed day; show the return day (+1) */}
                          <Text variant="bodySm">{r.period_from} → {r.period_to ? addDaysISO(r.period_to, 1) : "-"}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text variant="bodySm">{r.invoice?.invoice_number || "-"}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text variant="bodySm">{r.invoice?.total ? formatPrice(r.invoice.total) : "-"}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Badge size="small" tone={r.status === "stamped" ? "success" : r.status === "error" ? "critical" : "attention"}>
                            {r.status}
                          </Badge>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    ))}
                  </IndexTable>
                </LegacyCard>
              </BlockStack>
            </Card>
          )}
        </BlockStack>

        <Modal
          open={editModal}
          onClose={() => setEditModal(false)}
          title="Editar Grupo"
          primaryAction={{
            content: "Guardar",
            onAction: saveEdit,
            loading: savingEdit,
            disabled: !form.name?.trim(),
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setEditModal(false) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <TextField
                label="Nombre del Grupo"
                value={form.name || ""}
                onChange={(val) => setForm({ ...form, name: val })}
                autoComplete="off"
              />
              <TextField
                label="Empresa"
                value={company.id ? company.name : companySearch}
                onChange={async (val) => {
                  setCompanySearch(val);
                  setCompany({ id: null, name: "" });
                  if (val.trim().length < 2) { setCompanyResults([]); return; }
                  const { data } = await supabase
                    .from("companies")
                    .select("id, name")
                    .ilike("name", `%${val.trim()}%`)
                    .limit(5);
                  setCompanyResults(data || []);
                }}
                placeholder="Buscar empresa..."
                autoComplete="off"
              />
              {companyResults.length > 0 && !company.id && (
                <BlockStack gap="050">
                  {companyResults.map((c) => (
                    <Button key={c.id} variant="plain" onClick={() => {
                      setCompany({ id: c.id, name: c.name });
                      setCompanySearch("");
                      setCompanyResults([]);
                    }}>
                      {c.name}
                    </Button>
                  ))}
                </BlockStack>
              )}
              <Select
                label="Periodicidad"
                value={form.periodicity}
                options={[
                  { label: "1ero de cada mes", value: "monthly_1st" },
                  { label: "Último de cada mes", value: "monthly_last" },
                  { label: "Semanal", value: "weekly" },
                  { label: "Quincenal", value: "biweekly" },
                ]}
                onChange={(val) => setForm({ ...form, periodicity: val })}
              />
              {form.periodicity === "weekly" && (
                <Select
                  label="Día de la semana"
                  value={String(form.weekly_day)}
                  options={[
                    { label: "Lunes", value: "1" },
                    { label: "Martes", value: "2" },
                    { label: "Miércoles", value: "3" },
                    { label: "Jueves", value: "4" },
                    { label: "Viernes", value: "5" },
                    { label: "Sábado", value: "6" },
                    { label: "Domingo", value: "0" },
                  ]}
                  onChange={(val) => setForm({ ...form, weekly_day: parseInt(val) })}
                />
              )}
              <Select
                label="Periodo a facturar"
                value={form.billing_period}
                options={[
                  { label: "Transcurrido (uso pasado)", value: "transcurrido" },
                  { label: "Posterior (uso futuro)", value: "posterior", disabled: !form.credit_enabled },
                ]}
                onChange={(val) => setForm({ ...form, billing_period: val })}
              />
              <Select
                label="Crédito"
                value={form.credit_enabled ? "yes" : "no"}
                options={[
                  { label: "Sin crédito", value: "no" },
                  { label: "Con crédito", value: "yes" },
                ]}
                onChange={(val) => {
                  const enabled = val === "yes";
                  setForm({ ...form, credit_enabled: enabled, billing_period: !enabled && form.billing_period === "posterior" ? "transcurrido" : form.billing_period });
                }}
              />
              {form.credit_enabled && (
                <TextField
                  label="Días de crédito"
                  type="number"
                  value={String(form.credit_days || 0)}
                  onChange={(val) => setForm({ ...form, credit_days: parseInt(val) || 0 })}
                  suffix="días"
                />
              )}
              <Select
                label="Modo de facturación"
                value={form.auto_mode}
                options={[
                  { label: "Manual", value: "manual" },
                  { label: "Auto Borrador", value: "auto_draft" },
                  { label: "Auto Timbrar", value: "auto_stamp" },
                ]}
                onChange={(val) => setForm({ ...form, auto_mode: val })}
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default InvoicingGroupDetail;
